import { clearFontVars, type FontVars, showFontVars } from "./cssVars";
import { faceFamily, loadFace, loadMainFace, removeFaces, removeLocalFaces, renderedWeight } from "./faces";
import { type FontFitReport, measureFit, NO_FIT, scaleRatios } from "./fit";
import { type NamedFontResult, syncNamedFonts } from "./namedFonts";
import {
	type FontSettings,
	type FontSources,
	type LocalFontSlot,
	readFontState,
	SECONDARY_SLOTS,
	type SecondarySlot,
	scaleOf,
	setDraftFonts,
	sourcesOf,
	storeDesignFonts,
	weightOf,
} from "./settings";
import { delay } from "./timing";

export type LocalFontsResult = {
	loaded: LocalFontSlot[];
	failed: LocalFontSlot[];
	pending: boolean;
	fit: FontFitReport | null;
};

export type PreviewFontsResult = {
	main: LocalFontsResult | null;
	families: NamedFontResult[];
};

type FontRun = {
	id: number;
	key: string;
	sources: FontSources;
	settings: FontSettings;
	faces: FontFace[];
	mainFace: FontFace | null;
	secondary: Partial<Record<SecondarySlot, string>>;
	loaded: LocalFontSlot[];
	failed: LocalFontSlot[];
	report: FontFitReport | null;
	pending: boolean;
	dropped: boolean;
	settled: Promise<void>;
};

const DEFAULT_WAIT_MS = 800;
const PREVIEW_WAIT_MS = 15_000;

const inflight = new Map<string, FontRun>();
let lastRunId = 0;
let shown: FontRun | null = null;
let wanted: FontRun | null = null;

const isWanted = (run: FontRun) => !run.dropped && wanted === run;

const REGULAR_WEIGHT = 400;
const ACCENT_STEP = 200;

const activeWeightOf = ({ mainFace, secondary, settings }: FontRun) => {
	if (secondary.light || !mainFace) return null;
	const light = renderedWeight(mainFace, Number(weightOf(settings, "light")));
	if (light < renderedWeight(mainFace, REGULAR_WEIGHT)) return null;
	return String(renderedWeight(mainFace, Math.max(Number(weightOf(settings, "bold")), light + ACCENT_STEP)));
};

const varsOf = (run: FontRun): FontVars => {
	const { id, secondary, settings, report } = run;
	const main = faceFamily("main", id);
	return {
		activeWeight: activeWeightOf(run),
		families: { main, light: secondary.light ?? main, bold: secondary.bold ?? main },
		weights: {
			light: secondary.light ? null : weightOf(settings, "light"),
			bold: secondary.bold ? null : weightOf(settings, "bold"),
		},
		ratios: scaleRatios(settings.fit !== false && report ? report.ratios : NO_FIT, scaleOf(settings)),
	};
};

const paint = (run: FontRun) => showFontVars(varsOf(run));

const discard = (run: FontRun) => {
	run.dropped = true;
	if (inflight.get(run.key) === run) inflight.delete(run.key);
	removeFaces(run.faces);
};

const show = (run: FontRun) => {
	paint(run);
	const previous = shown;
	shown = run;
	if (previous && previous !== run) discard(previous);
};

const hide = () => {
	wanted = null;
	for (const run of [...inflight.values()]) discard(run);
	if (shown) discard(shown);
	shown = null;
	clearFontVars();
	removeLocalFaces();
};

const adopt = (run: FontRun, face: FontFace) => {
	if (run.dropped) return false;
	document.fonts.add(face);
	run.faces.push(face);
	return true;
};

const loadMain = async (run: FontRun) => {
	const family = faceFamily("main", run.id);
	const face = await loadMainFace(family, run.sources.main).catch(() => null);
	if (!face) {
		run.failed.push("main");
		if (isWanted(run)) hide();
		else discard(run);
		return;
	}
	if (!adopt(run, face) || !isWanted(run)) {
		discard(run);
		return;
	}
	run.mainFace = face;
	run.report = await measureFit(family);
	if (!isWanted(run)) {
		discard(run);
		return;
	}
	run.loaded.push("main");
	show(run);
};

const loadSecondary = async (run: FontRun, slot: SecondarySlot) => {
	const source = run.sources[slot];
	if (!source) return;
	const family = faceFamily(slot, run.id);
	const face = await loadFace(family, source).catch(() => null);
	if (!face) {
		run.failed.push(slot);
		return;
	}
	if (!adopt(run, face)) return;
	run.secondary[slot] = family;
	run.loaded.push(slot);
	if (shown === run) paint(run);
};

const loadRun = async (run: FontRun) => {
	const secondaries = SECONDARY_SLOTS.map((slot) => loadSecondary(run, slot));
	await loadMain(run);
	await Promise.all(secondaries);
};

const startRun = (key: string, sources: FontSources, settings: FontSettings) => {
	const run: FontRun = {
		id: ++lastRunId,
		key,
		sources,
		settings,
		faces: [],
		mainFace: null,
		secondary: {},
		loaded: [],
		failed: [],
		report: null,
		pending: true,
		dropped: false,
		settled: Promise.resolve(),
	};
	inflight.set(key, run);
	run.settled = loadRun(run)
		.catch(() => {
			if (shown !== run) discard(run);
		})
		.finally(() => {
			run.pending = false;
			if (inflight.get(key) === run) inflight.delete(key);
		});
	return run;
};

const runFor = (sources: FontSources, settings: FontSettings) => {
	const key = JSON.stringify(sources);
	if (shown?.key === key) return shown;
	return inflight.get(key) ?? startRun(key, sources, settings);
};

const resultOf = (run: FontRun): LocalFontsResult => ({
	loaded: [...run.loaded],
	failed: [...run.failed],
	pending: run.pending,
	fit: run.report,
});

export const resetLocalFonts = hide;

const loadMainFonts = async (settings: FontSettings, timeout: number): Promise<LocalFontsResult | null> => {
	const sources = sourcesOf(settings);
	if (!sources) {
		hide();
		return null;
	}
	const run = runFor(sources, settings);
	run.settings = settings;
	wanted = run;
	if (run === shown) paint(run);
	await Promise.race([run.settled, delay(timeout)]);
	return resultOf(run);
};

export const loadLocalFonts = (timeout = DEFAULT_WAIT_MS): Promise<LocalFontsResult | null> => {
	const { settings, families } = readFontState();
	void syncNamedFonts(families);
	return loadMainFonts(settings, timeout);
};

export const previewFonts = async (draft: FontSettings | null): Promise<PreviewFontsResult> => {
	setDraftFonts(draft);
	const { settings, families } = readFontState();
	const named = syncNamedFonts(families);
	const main = await loadMainFonts(settings, PREVIEW_WAIT_MS);
	return { main, families: await named };
};

export const syncDesignFonts = (design: Record<string, unknown> | null | undefined) => {
	if (storeDesignFonts(design)) void loadLocalFonts();
};
